import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { auth } from '../firebase';
import { updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import {
  FaBars, FaCalendarAlt, FaTimes, FaChevronRight, FaBell, FaBullhorn, FaChevronDown, FaChevronUp, FaLock,
} from 'react-icons/fa';
import '../PropertyManager/Navbar.css';
import './TenantNavbar.css';

const BASE_URL = 'https://api.rside.app';

function fmtLeaseDate(str) {
  if (!str) return '—';
  // MM-DD-YYYY
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) {
    const [m, d, y] = str.split('-');
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
  const d = new Date(str);
  return isNaN(d) ? str : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmt$(n) {
  return '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 0 });
}


function isAnnNew(ann) {
  const created = ann.created_at || ann.Created_At || ann.date_created;
  if (!created) return false;
  return (Date.now() - new Date(created).getTime()) < 7 * 24 * 60 * 60 * 1000;
}

const TenantNavbar = forwardRef(function TenantNavbar({ title = 'Home', tenantName = '', initials = '' }, ref) {
  const now = new Date();
  const dateStr = now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  const [showPanel, setShowPanel] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  // Change password state
  const [showChangePw, setShowChangePw] = useState(false);
  const [cpCurrent, setCpCurrent] = useState('');
  const [cpNew, setCpNew] = useState('');
  const [cpConfirm, setCpConfirm] = useState('');
  const [cpMsg, setCpMsg] = useState('');
  const [cpSaving, setCpSaving] = useState(false);


  const [showAnnounce, setShowAnnounce] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [annLoading, setAnnLoading] = useState(false);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [readAnnIds, setReadAnnIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('tenant_read_ann') || '[]')); }
    catch { return new Set(); }
  });
  const doorIdRef = useRef(null);

  const unreadCount = announcements.filter(a => {
    const id = a.id || a.announcement_id || a.ID;
    return !readAnnIds.has(id) && isAnnNew(a);
  }).length;

  const ensureDoorId = async () => {
    if (doorIdRef.current) return doorIdRef.current;
    const user = auth.currentUser;
    if (!user) return null;
    const res = await fetch(`${BASE_URL}/TenantApp/SessionInfo?email=${encodeURIComponent(user.email)}`);
    const s = await res.json();
    const id = s?.tenant?.door_id;
    doorIdRef.current = id;
    return id;
  };

  const fetchAnnouncements = async (silent = false) => {
    if (!silent) setAnnLoading(true);
    try {
      const doorId = await ensureDoorId();
      if (!doorId) return;
      const res = await fetch(`${BASE_URL}/GetAnnouncementsByDoor?door_id=${doorId}`);
      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : (data.announcements || data.Announcements || []);
        setAnnouncements(list);
      }
    } catch (err) { console.error('Error fetching announcements:', err); }
    finally { setAnnLoading(false); }
  };

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(user => {
      if (user) fetchAnnouncements(true);
    });
    return unsub;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openAnnouncePanel = async () => {
    setShowAnnounce(true);
    setShowPanel(false);
    await fetchAnnouncements();
    setReadAnnIds(prev => {
      const next = new Set(prev);
      announcements.forEach(a => next.add(a.id || a.announcement_id || a.ID));
      localStorage.setItem('tenant_read_ann', JSON.stringify([...next]));
      return next;
    });
  };

  const markAllRead = () => {
    const next = new Set(readAnnIds);
    announcements.forEach(a => next.add(a.id || a.announcement_id || a.ID));
    setReadAnnIds(next);
    localStorage.setItem('tenant_read_ann', JSON.stringify([...next]));
  };

  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setCpMsg('');
    if (cpNew !== cpConfirm) { setCpMsg('Passwords do not match.'); return; }
    if (cpNew.length < 6) { setCpMsg('Password must be at least 6 characters.'); return; }
    setCpSaving(true);
    try {
      const user = auth.currentUser;
      const credential = EmailAuthProvider.credential(user.email, cpCurrent);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, cpNew);
      setCpMsg('success');
      setCpCurrent(''); setCpNew(''); setCpConfirm('');
      setTimeout(() => { setShowChangePw(false); setCpMsg(''); }, 1500);
    } catch (err) {
      setCpMsg(err.code === 'auth/wrong-password' ? 'Current password is incorrect.' : 'Failed to update password.');
    } finally {
      setCpSaving(false);
    }
  };

  const openPanel = async () => {
    setShowPanel(true);
    if (profileData) return;
    setProfileLoading(true);
    try {
      const user = auth.currentUser;
      if (!user) return;
      const sessionRes = await fetch(`${BASE_URL}/TenantApp/SessionInfo?email=${encodeURIComponent(user.email)}`);
      const session = await sessionRes.json();
      const doorId = session?.tenant?.door_id;
      let leaseInfo = null;
      if (doorId) {
        const leaseRes = await fetch(`${BASE_URL}/Lease&TenantsByDoor?door_id=${encodeURIComponent(doorId)}`);
        const leaseData = await leaseRes.json();
        leaseInfo = leaseData?.tenants?.[0] || null;
      }
      setProfileData({
        email:  user.email,
        tenant: session?.tenant,
        pm:     session?.property_manager,
        lease:  leaseInfo,
      });
    } catch (err) {
      console.error(err);
    } finally {
      setProfileLoading(false);
    }
  };

  const closePanel = () => setShowPanel(false);

  useImperativeHandle(ref, () => ({
    openAnnouncements: openAnnouncePanel,
  }));

  return (
    <>
      <div className="navbar">
        <button
          className="navbar-hamburger"
          onClick={() => document.body.classList.toggle('sidebar-mobile-open')}
          aria-label="Toggle menu"
        >
          <FaBars />
        </button>

        <div className="navbar-left">
          <h1 className="navbar-title">{title}</h1>
          <div className="navbar-date-row">
            <FaCalendarAlt className="navbar-date-icon" />
            <span className="navbar-date-text">{dateStr}</span>
          </div>
        </div>

        <div className="navbar-center" />

        {/* Bell icon */}
        <button id="th-bell-btn" className="tnav-bell-btn" onClick={openAnnouncePanel} aria-label="Announcements">
          <FaBell />
          {unreadCount > 0 && <span className="tnav-bell-dot" />}
        </button>

        {/* Clickable user area */}
        <div className="navbar-right tnav-trigger" onClick={openPanel}>
          {tenantName && (
            <div className="navbar-user-info">
              <span className="navbar-user-name">{tenantName}</span>
              <span className="navbar-user-role">Tenant</span>
            </div>
          )}
          {initials && (
            <div className="navbar-avatar">{initials}</div>
          )}
        </div>
      </div>

      {/* Backdrop */}
      {(showPanel || showAnnounce) && (
        <div className="tnav-backdrop" onClick={() => { closePanel(); setShowAnnounce(false); }} />
      )}

      {/* Announcement slide panel */}
      <div className={`tnav-panel tnav-ann-panel ${showAnnounce ? 'tnav-panel-open' : ''}`}>
        <div className="tnav-panel-hd">
          <div className="tnav-ann-hd-left">
            <FaBullhorn className="tnav-ann-hd-icon" />
            <span className="tnav-panel-hd-title">Announcements</span>
            {announcements.length > 0 && (
              <span className="tnav-ann-count">{announcements.length}</span>
            )}
          </div>
          <button className="tnav-close-btn" onClick={() => setShowAnnounce(false)}><FaTimes /></button>
        </div>

        <div className="tnav-panel-scroll">
          {annLoading ? (
            <p className="tnav-empty-msg" style={{ padding: '24px 20px' }}>Loading announcements…</p>
          ) : announcements.length === 0 ? (
            <div className="tnav-ann-empty">
              <FaBullhorn className="tnav-ann-empty-icon" />
              <p>No announcements right now</p>
            </div>
          ) : (
            <div className="tnav-ann-list">
              {announcements.map((ann, i) => {
                const id = ann.id || ann.announcement_id || ann.ID || i;
                const isNew = isAnnNew(ann);
                const isRead = readAnnIds.has(id);
                const isExp = expandedIds.has(id);
                const msg = ann.message || ann.Message || ann.body || '';
                const title = ann.title || ann.Title || 'Announcement';
                const created = ann.created_at || ann.Created_At || ann.date_created || '';
                const isLong = msg.length > 120;
                const preview = isLong && !isExp ? msg.slice(0, 120) + '…' : msg;
                return (
                  <div key={id} className={`tnav-ann-item ${!isRead ? 'tnav-ann-unread' : ''}`}>
                    <div className="tnav-ann-item-hd">
                      <span className="tnav-ann-title">{title}</span>
                      {isNew && <span className="tnav-ann-new-badge">NEW</span>}
                    </div>
                    <p className="tnav-ann-msg">{preview}</p>
                    {isLong && (
                      <button className="tnav-ann-expand-btn" onClick={() => toggleExpand(id)}>
                        {isExp ? (
                          <><FaChevronUp style={{ fontSize: 10 }} /> Show less</>
                        ) : (
                          <><FaChevronDown style={{ fontSize: 10 }} /> Read more</>
                        )}
                      </button>
                    )}
                    {created && (
                      <span className="tnav-ann-date">
                        {new Date(created).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Account panel */}
      <div className={`tnav-panel ${showPanel ? 'tnav-panel-open' : ''}`}>

        {/* Panel header */}
        <div className="tnav-panel-hd">
          <span className="tnav-panel-hd-title">My Account</span>
          <button className="tnav-close-btn" onClick={closePanel}><FaTimes /></button>
        </div>

        <div className="tnav-panel-scroll">

          {/* Avatar + name + email */}
          <div className="tnav-profile-top">
            <div className="tnav-avatar-lg">{initials}</div>
            <h2 className="tnav-profile-name">{tenantName || '—'}</h2>
            {profileData?.email && (
              <p className="tnav-profile-email">{profileData.email}</p>
            )}
          </div>

          {/* YOUR LEASE */}
          <div className="tnav-section">
            <p className="tnav-section-label">YOUR LEASE</p>
            {profileLoading ? (
              <p className="tnav-empty-msg">Loading lease info…</p>
            ) : profileData?.lease ? (
              <div className="tnav-lease-card">
                <div className="tnav-lease-top">
                  <div>
                    <p className="tnav-lease-sub-label">ACTIVE LEASE</p>
                    <p className="tnav-lease-unit">Unit {profileData.lease.Unit_Number}</p>
                    <p className="tnav-lease-addr">{profileData.lease.Property_Name}</p>
                  </div>
                  <span className="tnav-active-badge">Active</span>
                </div>

                <div className="tnav-lease-grid">
                  <div className="tnav-lease-field">
                    <span className="tnav-lease-field-lbl">MONTHLY RENT</span>
                    <span className="tnav-lease-field-val">{fmt$(profileData.lease.Rent)}</span>
                  </div>
                  <div className="tnav-lease-field">
                    <span className="tnav-lease-field-lbl">LEASE START</span>
                    <span className="tnav-lease-field-val">{fmtLeaseDate(profileData.lease.Start_Date)}</span>
                  </div>
                  <div className="tnav-lease-field">
                    <span className="tnav-lease-field-lbl">LEASE END</span>
                    <span className="tnav-lease-field-val">{fmtLeaseDate(profileData.lease.End_Date)}</span>
                  </div>
                </div>

                {profileData.pm && (
                  <div className="tnav-lease-pm">
                    <div className="tnav-lease-pm-info">
                      <span className="tnav-lease-field-lbl">MANAGED BY</span>
                      <span className="tnav-lease-pm-name">
                        {profileData.pm.full_name}
                        {profileData.pm.phone && ` · ${profileData.pm.phone}`}
                      </span>
                    </div>
                    {profileData.pm.phone && (
                      <a href={`tel:${profileData.pm.phone}`} className="tnav-contact-btn">
                        Contact
                      </a>
                    )}
                  </div>
                )}
              </div>
            ) : !profileLoading && profileData ? (
              <p className="tnav-empty-msg">No active lease found.</p>
            ) : null}
          </div>

          {/* ACCOUNT */}
          <div className="tnav-section">
            <p className="tnav-section-label">ACCOUNT</p>
            <div className="tnav-card">
              <div
                className="tnav-row"
                style={{ cursor: 'pointer' }}
                onClick={() => { setShowChangePw(v => !v); setCpMsg(''); }}
              >
                <div className="tnav-row-info">
                  <span className="tnav-row-label">Change password</span>
                  <span className="tnav-row-sub">Update your login credentials</span>
                </div>
                <FaChevronRight className="tnav-row-chevron" style={{ transform: showChangePw ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }} />
              </div>

              {showChangePw && (
                <form onSubmit={handleChangePassword} style={{ padding: '12px 16px 16px', borderTop: '1px solid #f3f4f6' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                    <FaLock style={{ fontSize: 12, color: '#9ca3af' }} />
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Change Password</span>
                  </div>
                  <input
                    type="password"
                    placeholder="Current password"
                    value={cpCurrent}
                    onChange={e => setCpCurrent(e.target.value)}
                    required
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 13, marginBottom: 8, boxSizing: 'border-box' }}
                  />
                  <input
                    type="password"
                    placeholder="New password"
                    value={cpNew}
                    onChange={e => setCpNew(e.target.value)}
                    required
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 13, marginBottom: 8, boxSizing: 'border-box' }}
                  />
                  <input
                    type="password"
                    placeholder="Confirm new password"
                    value={cpConfirm}
                    onChange={e => setCpConfirm(e.target.value)}
                    required
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 13, marginBottom: 10, boxSizing: 'border-box' }}
                  />
                  {cpMsg && (
                    <p style={{ fontSize: 12, margin: '0 0 8px', color: cpMsg === 'success' ? '#16a34a' : '#dc2626' }}>
                      {cpMsg === 'success' ? 'Password updated successfully!' : cpMsg}
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="submit"
                      disabled={cpSaving}
                      style={{ flex: 1, padding: '8px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', fontSize: 13, fontWeight: 600, cursor: cpSaving ? 'not-allowed' : 'pointer' }}
                    >
                      {cpSaving ? 'Saving…' : 'Update Password'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowChangePw(false); setCpMsg(''); }}
                      style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, cursor: 'pointer' }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>

          {/* Contact */}
          <div className="tnav-section tnav-section-last" style={{ textAlign: 'center', padding: '4px 0 8px' }}>
            <p style={{ fontSize: 12, color: '#9ca3af', margin: 0 }}>
              We would love to hear from you at{' '}
              <a href="mailto:devTeam@dtecx.net" style={{ color: '#2563eb', fontWeight: 600, textDecoration: 'none' }}>devTeam@dtecx.net</a>
            </p>
          </div>

        </div>
      </div>
    </>
  );
});

export default TenantNavbar;
